/*
 * Sample contracts and subscriptions (P5.3, P5.4). A contract is what the
 * customer signed; its lines are the subscriptions — product, package,
 * licences, period and price — and its end date is the renewal date the
 * renewal reminders count down to.
 *
 * status: draft | pending_approval | active | expiring | expired | terminated | superseded
 * Contract values are sensitive (Settings → Field Security): roles without
 * "view commercial values" see them masked.
 */
import { daysFromNow, daysUntil } from "@/helpers/crm";
import { packages } from "@/mock/masters";
import { customers } from "@/mock/customers";

// The package a segment normally buys, per product.
// HRM by employee band, LMS by loan-book size, standalone systems on a support contract. Accounting is not on sale yet.
const PKG = {
  1: { SME: 1, MID: 2, ENT: 3, STR: 3 },
  2: { SME: 4, MID: 4, ENT: 5, STR: 5 },
  4: { SME: 9, MID: 9, ENT: 9, STR: 9 },
  5: { SME: 10, MID: 10, ENT: 10, STR: 10 },
};

// What former customers paid before they left (their MRR is now zero).
const LAST_MRR = { 14: 55000, 17: 18000 };

// Contract lines: the customer's MRR spread over its products in proportion to list price.
const linesFor = (c, start, end) => {
  const mrr = c.mrr || LAST_MRR[c.id] || 0;
  const pk = c.product_ids.map((pid) => packages.find((p) => p.id === PKG[pid][c.segment]));
  const list = pk.reduce((s, p) => s + p.price, 0);
  return pk.map((p) => {
    const net = Math.round((mrr * p.price) / list / 100) * 100;
    // One package per tenant; licences are employees (HRM) or staff users (LMS).
    const qty = 1;
    return {
      product_id: p.product_id,
      package_id: p.id,
      quantity: qty,
      licences: Math.max(p.users, Math.round((p.users * net) / p.price / 10) * 10),
      list_price: p.price * qty,
      net_monthly: net,
      discount_pct: net < p.price * qty ? Math.round((1 - net / (p.price * qty)) * 100) : 0,
      start,
      end,
    };
  });
};

const statusFor = (c, end) => {
  if (c.lifecycle === "former") return "terminated";
  const d = daysUntil(end);
  if (d < 0) return "expired";
  // A company setting (Settings → Customer Success): contracts within 60 days of end show as expiring.
  return d <= 60 ? "expiring" : "active";
};

let seq = 300;
const contractFor = (c, extra = {}) => {
  const end = c.renewal_date ?? c.exited_on;
  const start = daysFromNow(daysUntil(end) - 365);
  const lines = linesFor(c, start, end);
  const monthly = lines.reduce((s, l) => s + l.net_monthly, 0);
  seq += 1;
  return {
    id: seq - 300,
    code: `CON-${new Date(start).getFullYear()}-${String(seq).padStart(4, "0")}`,
    customer_id: c.id,
    title: `${c.product_ids.length > 1 ? "Subscription agreement" : "Software subscription"} · ${c.name}`,
    type: daysUntil(c.customer_since) < -400 ? "Renewal" : "New business",
    billing: c.segment === "ENT" ? "Annual in advance" : "Monthly",
    term_months: 12,
    signed_on: daysFromNow(daysUntil(start) - 6),
    start_date: start,
    end_date: end,
    renewal_date: c.renewal_date,
    auto_renew: c.segment !== "ENT",
    notice_days: 60,
    po_number: c.segment === "SME" ? null : `PO-${c.code.slice(4)}-${String(1000 + c.id * 37).slice(-4)}`,
    monthly_value: monthly,
    value: monthly * 12,
    status: statusFor(c, end),
    owner_id: c.account_manager_id,
    approval: { status: "approved", by: "Brian Otieno", at: daysFromNow(daysUntil(start) - 8), level: monthly * 12 > 1500000 ? "Head of Sales + Finance" : "Sales Manager" },
    lines,
    documents: [
      { name: `${c.code} signed agreement.pdf`, type: "Contract", size: "1.4 MB", at: daysFromNow(daysUntil(start) - 6), by: "Jane Wanjiku" },
      ...(c.segment !== "SME" ? [{ name: `Purchase order ${c.code.slice(4)}.pdf`, type: "Purchase order", size: "320 KB", at: daysFromNow(daysUntil(start) - 4), by: "Mercy Wambui" }] : []),
      { name: "Service level agreement.pdf", type: "Agreement", size: "640 KB", at: daysFromNow(daysUntil(start) - 6), by: "Jane Wanjiku" },
    ],
    amendments: [],
    ...extra,
  };
};

export const contracts = customers.map((c) => contractFor(c));

// A few with a story of their own.
const byCustomer = (id) => contracts.find((k) => k.customer_id === id);

// Bidii added 40 HR licences mid-term; the amendment is waiting on Finance.
Object.assign(byCustomer(5), {
  amendments: [
    { at: daysFromNow(-3), change: "Add 40 HR & Payroll licences (Standard) from next month", value_change: 9600 * 12, by: "Jane Wanjiku", status: "pending_approval", approver: "Mercy Wambui (Finance)" },
  ],
});
// Savannah: renewal at a higher price, still in draft.
Object.assign(byCustomer(1), {
  amendments: [
    { at: daysFromNow(-120), change: "Price review: +5% on DeeHr360 Advanced from month 7", value_change: 120000 * 0.05 * 6, by: "Jane Wanjiku", status: "approved", approver: "Brian Otieno" },
  ],
});
// Rift Valley Millers: signed recently, approval took two levels.
Object.assign(byCustomer(8), {
  approval: { status: "approved", by: "Mercy Wambui", at: daysFromNow(-27), level: "Head of Sales + Finance" },
  type: "New business",
});
// Tumaini: notice of termination received.
Object.assign(byCustomer(12), { auto_renew: false, notice_received_on: daysFromNow(-25) });

let extra = 100;
const history = (cid, yearsBack, status = "superseded") => {
  const cur = byCustomer(cid);
  extra += 1;
  return {
    ...structuredClone(cur),
    id: extra,
    code: `CON-${new Date(cur.start_date).getFullYear() - yearsBack}-${String(extra + 100).padStart(4, "0")}`,
    type: yearsBack > 1 ? "New business" : "Renewal",
    start_date: daysFromNow(daysUntil(cur.start_date) - 365 * yearsBack),
    end_date: daysFromNow(daysUntil(cur.end_date) - 365 * yearsBack),
    renewal_date: null,
    monthly_value: Math.round(cur.monthly_value * (1 - 0.06 * yearsBack)),
    value: Math.round(cur.monthly_value * (1 - 0.06 * yearsBack)) * 12,
    status,
    amendments: [],
    superseded_by: cur.code,
  };
};

// Earlier terms of the longest-standing customers.
contracts.push(history(1, 1), history(9, 1), history(9, 2), history(18, 1), history(18, 2));

// A cross-sell for Gelisa: DeeHr360 for its own staff, drafted and waiting for the Sales Manager.
contracts.push({
  ...structuredClone(byCustomer(19)),
  id: 120,
  code: "CON-2026-0420",
  title: "DeeHr360 HR & Payroll add-on · Gelisa",
  type: "Cross-sell",
  status: "pending_approval",
  signed_on: null,
  start_date: daysFromNow(10),
  end_date: daysFromNow(140),
  renewal_date: daysFromNow(140),
  monthly_value: 22500,
  value: 22500 * 4,
  term_months: 4,
  approval: { status: "pending", by: null, at: null, level: "Sales Manager", requested_by: "Brian Otieno", requested_at: daysFromNow(-1), note: "10% discount to co-term with the LMS contract" },
  lines: [{ product_id: 1, package_id: 1, quantity: 1, licences: 50, list_price: 25000, net_monthly: 22500, discount_pct: 10, start: daysFromNow(10), end: daysFromNow(140) }],
  documents: [{ name: "Draft HRM add-on agreement v2.docx", type: "Agreement", size: "210 KB", at: daysFromNow(-1), by: "Brian Otieno" }],
  amendments: [],
});

export const contractById = (id) => contracts.find((k) => k.id === Number(id));
export const contractsFor = (customerId) => contracts.filter((k) => k.customer_id === Number(customerId));
export const currentContract = (customerId) => contracts.find((k) => k.customer_id === Number(customerId) && !["superseded", "pending_approval", "draft"].includes(k.status));

export const contractStatuses = [
  { key: "draft", name: "Draft", tone: "secondary" },
  { key: "pending_approval", name: "Pending approval", tone: "warning" },
  { key: "active", name: "Active", tone: "success" },
  { key: "expiring", name: "Expiring", tone: "warning" },
  { key: "expired", name: "Expired", tone: "danger" },
  { key: "terminated", name: "Terminated", tone: "dark" },
  { key: "superseded", name: "Superseded", tone: "light" },
];
export const contractStatus = (key) => contractStatuses.find((s) => s.key === key) ?? { key, name: key, tone: "secondary" };
